import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'

import { useLandingHeaderVariant } from '@pages/landing/model/useLandingHeaderVariant'

import { useLandingPage } from '@entities/LandingPage'

import { LandingPageBuilder } from '@features/landing-page-builder'


import NotFoundComponent from '@widgets/not-found/NotFoundComponent'

import SEO from '@shared/components/SEO'
import Container from '@shared/ui/container'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

function LandingSkeleton () {
  return (
    <Container>
      <Wrapper className={styles.wrapper}>
        <SkeletonTheme>
          <Skeleton height={190} style={{ borderRadius: 24, marginBottom: 24 }} />
          <Skeleton width="40%" height={28} style={{ marginBottom: 16 }} />
          <div className={styles.skeleton__row}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} height={180} style={{ borderRadius: 16 }} />
            ))}
          </div>
        </SkeletonTheme>
      </Wrapper>
    </Container>
  )
}

/*
  Renders a backend-configured landing page by its code (/landing/:code).

  The page is a thin shell on purpose: the backend decides which blocks exist,
  in what order and for which state, and the block mapper renders them. Adding a
  block type is a backend change plus a registry entry — not a new page.
*/
export default function LandingPage () {
  const { code } = useParams<{ code: string }>()

  const { landingPage, loading } = useLandingPage({ code })

  // Swaps the layout chrome for what the landing asks for (custom / none / site
  // header) and restores it on leave. Runs before the early returns — hooks
  // cannot be conditional.
  useLandingHeaderVariant(landingPage?.header, Boolean(landingPage))

  // First load, nothing cached yet.
  if (loading && !landingPage) {
    return <LandingSkeleton />
  }

  // No such landing, or it is no longer active.
  if (!landingPage) {
    return (
      <Container>
        <Wrapper className={styles.wrapper}>
          <NotFoundComponent />
        </Wrapper>
      </Container>
    )
  }

  const { name, blocks, actions, header, promo } = landingPage

  // A hero-style banner block already opens the page with a headline.
  const hasHeroBlock = blocks.some(
    (block) => block.type === 'banner' && Boolean((block.payload as { title?: string } | null)?.title),
  )
  const showPageTitle = header?.variant !== 'custom' && !hasHeroBlock

  return (
    <Container>
      {name && <SEO title={name} description={name} />}

      <Wrapper className={styles.wrapper}>
        {/*
          The landing's name as a page title. Skipped for the custom header,
          which already carries the landing's identity as its logo, and when a
          hero block opens the page with its own headline.
        */}
        {showPageTitle && name && <h1 className={styles.title}>{name}</h1>}

        <LandingPageBuilder blocks={blocks} actions={actions} promo={promo} />
      </Wrapper>
    </Container>
  )
}
